import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { AuthLayout } from "@/pages/Login";
import { useLanguage } from "@/context/LanguageContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(t("reset_done"));
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t("invalid_reset"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm sm:p-8" data-testid="reset-card">
        <Link to="/login" className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" /> {t("back_to_login")}
        </Link>
        <h1 className="font-display text-2xl font-bold text-slate-900">{t("reset_title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("reset_subtitle")}</p>

        {!token ? (
          <p className="mt-6 rounded-xl bg-rose-50 p-3 text-sm text-rose-700" data-testid="reset-no-token">{t("invalid_reset")}</p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="new-password">{t("new_password")}</Label>
              <div className="relative mt-1.5">
                <Input id="new-password" type={show ? "text" : "password"} required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" className="min-h-[48px] pr-12" autoComplete="new-password" />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label={show ? t("hide") : t("show")}>
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-400">Minimum 8 characters.</p>
            </div>
            {error && <p className="text-sm text-rose-600" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="reset-submit-btn" className="min-h-[48px] w-full rounded-xl font-semibold">
              {loading ? t("loading") : t("reset_btn")}
            </Button>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}
